import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactApexChart from 'react-apexcharts';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faChartBar } from '@fortawesome/free-solid-svg-icons';

const IndexChart = ({ config }) => {
    const [chartData, setChartData] = useState(null);
    const [indexes, setIndexes] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState( config.defaultIndexChart || 'NIFTY-50');
    const [duration, setDuration] = useState( config.indexChartDuration || '1Y');
    const [loading, setLoading] = useState(true);
    const [chartType, setChartType] = useState( config.indexChartType || 'candlestick');

    const durations = [
        { label: '1M', value: '1M' },
        { label: '3M', value: '3M' },
        { label: '6M', value: '6M' },
        { label: '1Y', value: '1Y' },
        { label: '3Y', value: '3Y' },
        { label: '5Y', value: '5Y' }
    ];

    useEffect(() => {
        fetchIndexes();
    }, []);

    useEffect(() => {
        if (selectedIndex) {
            fetchIndexData();
        }
    }, [selectedIndex, duration, chartType]);

    const fetchIndexes = async () => {
        try {
            console.log('Fetching indexes...');
            const response = await axios.get(`${config.PY_API_URL}api/indexes`);
            if (response.data.status === 'success') {
                console.log('Indexes fetched successfully:', response.data.data.indexes);
                setIndexes(response.data.data.indexes);
            } else {
                console.error('Failed to fetch indexes:', response.data);
            }
        } catch (error) {
            console.error('Error fetching indexes:', error);
        }
    };

    const fetchIndexData = async () => {
        try {
            console.log(`Fetching index data for ${selectedIndex} with duration ${duration}...`);
            setLoading(true);
            const response = await axios.get(`${config.PY_API_URL}api/index/data`, {
                params: {
                    index_key: selectedIndex,
                    duration: duration
                }
            });

            if (response.data.status === 'success') {
                console.log('Index data fetched successfully:', response.data.data.chart_data);
                const data = response.data.data.chart_data;
                
                if (chartType === 'line') {
                    setChartData({
                        series: [{
                            name: 'Close',
                            data: data.map(d => ({
                                x: new Date(d.date).getTime(),
                                y: parseFloat(d.close)
                            }))
                        }],
                        options: {
                            chart: {
                                type: 'area',
                                height: 400,
                                toolbar: {
                                    show: false
                                },
                                animations: {
                                    enabled: false
                                },
                                background: '#ffffff'
                            },
                            stroke: {
                                curve: 'smooth',
                                width: 2,
                                colors: ['#92e5ac']
                            },
                            fill: {
                                type: 'solid',
                                gradient: {
                                    shadeIntensity: 1,
                                    inverseColors: false,
                                    opacityFrom: 0.7,
                                    opacityTo: 0.2,
                                    stops: [0, 90, 100]
                                },
                                colors: ['#92e5ac']
                            },
                            colors: ['#92e5ac'],
                            grid: {
                                show: true,
                                borderColor: '#f1f1f1',
                                strokeDashArray: 0,
                                position: 'back',
                                xaxis: {
                                    lines: {
                                        show: false
                                    }
                                },
                                yaxis: {
                                    lines: {
                                        show: true,
                                        opacity: 0.1
                                    }
                                }
                            },
                            states: {
                                hover: {
                                    filter: {
                                        type: 'none'
                                    }
                                }
                            },
                            xaxis: {
                                type: 'datetime',
                                labels: {
                                    style: {
                                        colors: '#666',
                                        fontSize: '12px'
                                    },
                                    datetimeFormatter: {
                                        year: 'yyyy',
                                        month: "MMM 'yy",
                                        day: 'dd MMM',
                                    }
                                },
                                axisBorder: {
                                    show: true,
                                    color: '#e0e0e0'
                                },
                                axisTicks: {
                                    show: true,
                                    color: '#e0e0e0'
                                }
                            },
                            yaxis: {
                                labels: {
                                    formatter: function(val) {
                                        return val.toFixed(2)
                                    },
                                    style: {
                                        colors: '#666',
                                        fontSize: '12px'
                                    }
                                },
                                axisBorder: {
                                    show: true,
                                    color: '#e0e0e0'
                                }
                            },
                            tooltip: {
                                theme: 'light',
                                x: {
                                    format: 'dd MMM yyyy'
                                },
                                y: {
                                    formatter: function(val) {
                                        return val.toFixed(2)
                                    },
                                    title: {
                                        formatter: () => 'Close: '
                                    }
                                },
                                marker: {
                                    show: true
                                }
                            },
                            dataLabels: {
                                enabled: false
                            }
                        }
                    });
                } else {
                    setChartData({
                        series: [{
                            data: data.map(d => ({
                                x: new Date(d.date).getTime(),
                                y: [
                                    parseFloat(d.open),
                                    parseFloat(d.high),
                                    parseFloat(d.low),
                                    parseFloat(d.close)
                                ]
                            }))
                        }],
                        options: {
                            chart: {
                                type: 'candlestick',
                                height: 400,
                                toolbar: {
                                    show: false
                                }
                            },
                            plotOptions: {
                                candlestick: {
                                    colors: {
                                        upward: '#26a69a',
                                        downward: '#ef5350'
                                    },
                                    wick: {
                                        useFillColor: true,
                                    }
                                }
                            },
                            xaxis: {
                                type: 'datetime'
                            },
                            yaxis: {
                                tooltip: {
                                    enabled: true
                                },
                                labels: {
                                    formatter: function(val) {
                                        return val.toFixed(2)
                                    }
                                }
                            }
                        }
                    });
                }
            } else {
                console.error('Failed to fetch index data:', response.data);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching index data:', error);
            setLoading(false);
        }
    };

    return (
        <div className="index-chart-container">
            <div className="chart-header">
                <h3 className="chart-title">Index Performance</h3>
                <div className="chart-controls">
                    <select 
                        value={selectedIndex}
                        onChange={(e) => setSelectedIndex(e.target.value)}
                        className="index-select"
                    >
                        {indexes.map(index => (
                            <option key={index.index_key} value={index.index_key}>
                                {index.name}
                            </option>
                        ))}
                    </select>

                    <div className="chart-type-toggle">
                        <span 
                            className={`type-btn ${chartType === 'line' ? 'active' : ''}`}
                            onClick={() => setChartType('line')}
                            title="Line Chart"
                        >
                            <FontAwesomeIcon icon={faChartLine} />
                        </span>
                        <span 
                            className={`type-btn ${chartType === 'candlestick' ? 'active' : ''}`}
                            onClick={() => setChartType('candlestick')}
                            title="Candlestick Chart"
                        >
                            <FontAwesomeIcon icon={faChartBar} />
                        </span>
                    </div>
                </div>
            </div>

            <div className="chart-wrapper" style={{ height: '400px' }}>
                {loading ? (
                    <div className="chart-loading">Loading chart...</div>
                ) : chartData ? (
                    <ReactApexChart 
                        options={chartData.options}
                        series={chartData.series}
                        type={chartType === 'line' ? 'line' : 'candlestick'}
                        height={400}
                    />
                ) : (
                    <div className="chart-error">No data available</div>
                )}
            </div>

            <div className="chart-footer">
                <div className="duration-buttons">
                    {durations.map(d => (
                        <span
                            key={d.value}
                            className={`duration-btn ${duration === d.value ? 'active' : ''}`}
                            onClick={() => setDuration(d.value)}
                        >
                            {d.label}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default IndexChart; 